// Copyright (c) 2022-2024. Sendanor <info@sendanor.fi>. All rights reserved.

import { VIRTUAL_SERVER_INTRO_VIEW_CLASS_NAME } from "../../../../../core/constants/className";
import { SENDANOR_VPS_WEBSITE } from "../../../../../core/constants/company";
import {
    T_ORDER_NOW,
    T_SITE_NAME,
    T_VIRTUALSERVERS_DESCRIPTION,
    T_VIRTUALSERVERS_INFO_WEBSITE_LINK_TEXT,
    T_VIRTUALSERVERS_MAIN_TITLE,
    T_VIRTUALSERVERS_PRODUCT_LINK_TEXT,
    T_VIRTUALSERVERS_SEO_IMAGE_EXTRA,
    T_VIRTUALSERVERS_TITLE,
} from "../../../../../core/constants/translation";
import {
    VIRTUALSERVERS_QUERY_PARAM_PRICE_TYPE,
    VIRTUALSERVERS_QUERY_PARAM_PRODUCT_ID,
    VIRTUALSERVERS_INTRO_ROUTE,
    VIRTUALSERVERS_PRODUCT_ROUTE,
} from "../../../../../core/constants/route";
import { ProductTableItemModel } from "../../../../../../../io/hyperify/core/store/types/product/ProductTableItemModel";
import { ProductType } from "../../../../../../../io/hyperify/core/store/types/product/ProductType";
import { Product } from "../../../../../../../io/hyperify/core/store/types/product/Product";
import { parseProductPriceType, ProductPriceType } from "../../../../../../../io/hyperify/core/store/types/product/ProductPriceType";
import { ProductUtils } from "../../../../../../../io/hyperify/core/store/utils/ProductUtils";
import { useStoreProductList } from "../../../../../../../io/hyperify/frontend/hooks/useStoreProductList";
import { SEODiv } from "../../../common/seo/SEODiv";
import { ArticleHeader } from "../../../common/title/ArticleHeader";
import { find } from "../../../../../../../io/hyperify/core/functions/find";
import { DEFAULT_SELECTED_PRICE_TYPE_FOR_VPS } from "../../../../../core/constants/frontend";
import { TranslationFunction } from "../../../../../../../io/hyperify/core/types/TranslationFunction";
import { shuffle } from "../../../../../../../io/hyperify/core/functions/shuffle";
import { first } from "../../../../../../../io/hyperify/core/functions/first";
import { SendanorSeoImageData } from "../../../../../core/types/SendanorSeoImageData";
import { getSeoDataFromProductTableItem } from "../utils/getSeoDataFromProductTableItem";
import { useQueryParam } from "../../../../../../../io/hyperify/frontend/hooks/useQueryParams";
import "./VirtualServerIntroView.scss";

// const LOG = LogService.createLogger('VirtualServerIntroView');

export interface VirtualServerIntroViewProps {
    readonly t?: TranslationFunction;
    readonly className?: string;
    readonly button?: string;
}

export function VirtualServerIntroView (props: VirtualServerIntroViewProps) {

    const t = props?.t ?? ((key: string): string => key);
    const className = props?.className;
    const productList : Product[] = useStoreProductList(ProductType.VIRTUAL_SERVER);

    const [queryParamPriceType] = useQueryParam(VIRTUALSERVERS_QUERY_PARAM_PRICE_TYPE, DEFAULT_SELECTED_PRICE_TYPE_FOR_VPS);
    const priceType : ProductPriceType = parseProductPriceType(queryParamPriceType) ?? DEFAULT_SELECTED_PRICE_TYPE_FOR_VPS;

    const productTableItemsList: ProductTableItemModel[] = ProductUtils.createProductTableItemsList(
        T_VIRTUALSERVERS_TITLE,
        "",
        priceType,
        productList
    );

    // Use one item by random as the default seo image featured item
    const [productIdQueryParam, ] = useQueryParam(VIRTUALSERVERS_QUERY_PARAM_PRODUCT_ID, '');

    const [, ...featuredItems] : ProductTableItemModel[] = productTableItemsList;
    const productIdFeaturedItem : ProductTableItemModel | undefined = (
        productIdQueryParam
            ? find(
                featuredItems,
                (item: ProductTableItemModel) : boolean => !!item?.productModel?.id && (item?.productModel?.id === productIdQueryParam)
            )
            : undefined
    );
    const seoImageItem : ProductTableItemModel | undefined = productIdFeaturedItem ?? first(shuffle(featuredItems));
    const seoFeaturedItemImageData : SendanorSeoImageData = getSeoDataFromProductTableItem(
        t,
        seoImageItem,
        undefined,
        t(T_ORDER_NOW),
        t(T_VIRTUALSERVERS_TITLE),
        t(T_VIRTUALSERVERS_SEO_IMAGE_EXTRA),
    );

    return (
        <SEODiv
            className={
                VIRTUAL_SERVER_INTRO_VIEW_CLASS_NAME
                + (className ? ` ${className}` : "")
            }
            path={VIRTUALSERVERS_INTRO_ROUTE}
            title={seoFeaturedItemImageData.title ?? ''}
            description={seoFeaturedItemImageData.description ?? ''}
            siteName={t(T_SITE_NAME)}
            tagLine={seoFeaturedItemImageData.tagLine}
            price={seoFeaturedItemImageData.price}
            buttonText={seoFeaturedItemImageData.buttonText}
            discountText={seoFeaturedItemImageData.discountText}
            extraInfo={seoFeaturedItemImageData.extraInfo}
        >
            <article className={VIRTUAL_SERVER_INTRO_VIEW_CLASS_NAME +'-article'}>
                <ArticleHeader
                    title={t(T_VIRTUALSERVERS_MAIN_TITLE)}
                    description={t(T_VIRTUALSERVERS_DESCRIPTION)}
                    className={VIRTUAL_SERVER_INTRO_VIEW_CLASS_NAME+'-article-header'}
                    t={t}
                    dark={true}
                    primaryButtonTo={VIRTUALSERVERS_PRODUCT_ROUTE}
                    primaryButtonText={t(T_VIRTUALSERVERS_PRODUCT_LINK_TEXT)}
                    secondaryButtonTo={SENDANOR_VPS_WEBSITE}
                    secondaryButtonText={t(T_VIRTUALSERVERS_INFO_WEBSITE_LINK_TEXT)}
                />
            </article>
        </SEODiv>
    );
}
